import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { t } from "ttag";
import { Button, ChevronLeft, IconButton, Select } from "@coolremote/shared";
import { isQaBypassCredentials } from "../../../models/devicePublicRegistrationTestBypass";
import { ExistingUserFlowState, hasCustomerSelected } from "./existingUserFlowState";
import { SiteSummary, associateDevice, listSites } from "./existingUserFlowApi";
import parentStyles from "../DeviceRegistrationPublic.module.css";
import styles from "./ExistingUserFlow.module.css";

const CREATE_NEW_SITE_VALUE = "__create_new_site__";

/**
 * Step 3 of the existing-user branch (CM-619): pick one of the selected
 * customer's sites, or create one. Same "Option A" as Select Customer — the
 * dropdown is shown even when the customer has no sites, with only
 * "+ Create new site" in it.
 */
const SelectSiteStep: React.FC = () => {
  const history = useHistory<ExistingUserFlowState | undefined>();
  const deviceState = history.location.state;
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState(deviceState?.siteId ?? "");
  const [isAssociating, setIsAssociating] = useState(false);
  const [associateError, setAssociateError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!hasCustomerSelected(deviceState)) {
      history.replace("/register-device/existing-user/select-customer");
      return;
    }
    let active = true;
    setIsLoadingSites(true);
    listSites(deviceState.customerId).then((result) => {
      if (active) {
        setSites(result);
        setIsLoadingSites(false);
        // QA bypass — pre-select the first site so the wizard can be clicked
        // through end-to-end without a real choice to make.
        if (!deviceState.siteId && isQaBypassCredentials(deviceState.serial, deviceState.pin) && result[0]) {
          setSelectedSiteId(result[0].siteId);
        }
      }
    });
    return () => {
      active = false;
    };
    // Re-fetch whenever the selected customer changes (e.g. switching back
    // and picking a different one) — the previous customer's sites must not
    // linger in the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceState?.customerId]);

  if (!hasCustomerSelected(deviceState)) {
    return null;
  }

  const handleBack = () => {
    history.goBack();
  };

  // A just-created site (from NewSiteStep) may not be in `listSites`'s
  // response yet — merge it in so the trigger can actually display it as selected.
  const knownSites =
    deviceState.siteId &&
    deviceState.siteName &&
    !sites.some((site) => site.siteId === deviceState.siteId)
      ? [{ siteId: deviceState.siteId, name: deviceState.siteName }, ...sites]
      : sites;

  const handleSelectSite = (siteId: string) => {
    if (siteId === CREATE_NEW_SITE_VALUE) {
      history.push("/register-device/existing-user/site-details", deviceState);
      return;
    }
    setSelectedSiteId(siteId);
  };

  const handleNext = async () => {
    const site = knownSites.find((candidate) => candidate.siteId === selectedSiteId);
    if (!site) {
      return;
    }
    setAssociateError(undefined);
    setIsAssociating(true);
    try {
      const { deviceId } = await associateDevice(
        site.siteId,
        deviceState.serial,
        deviceState.pin,
        deviceState.description
      );
      const nextState = {
        ...deviceState,
        siteId: site.siteId,
        siteName: site.name,
        deviceId
      } as ExistingUserFlowState;
      // Attach the chosen site to *this* history entry too, so that going back
      // from Discovery lands here with the choice restored.
      history.replace(history.location.pathname, nextState);
      history.push("/register-device/existing-user/discovery", nextState);
      // No setIsAssociating(false) here — the push above unmounts this component.
    } catch (error: unknown) {
      setAssociateError(error instanceof Error ? error.message : t`Something went wrong. Please try again`);
      setIsAssociating(false);
    }
  };

  const siteOptions = [
    ...knownSites.map((site) => ({ value: site.siteId, label: site.name })),
    { value: CREATE_NEW_SITE_VALUE, label: t`+ Create new site` }
  ];

  return (
    <div className={parentStyles.screen}>
      <div className={parentStyles.appBar}>
        <IconButton className={parentStyles.backButton} onClick={handleBack} aria-label={t`Back`}>
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={parentStyles.appBarTitle}>{t`Select Site`}</span>
        <span />
      </div>

      <div className={styles.stepContent}>
        <p className={styles.stepSubtitle}>{t`Choose the site for this device:`}</p>
        {associateError && <div className={styles.errorBanner}>{associateError}</div>}
        <Select
          fullWidth
          searchable
          menuPortal
          maxMenuWidth="calc(100vw - 48px)"
          disabled={isLoadingSites}
          value={selectedSiteId}
          onChange={handleSelectSite}
          options={siteOptions}
        />
        <Button
          variant="contained"
          className={styles.primaryButton}
          disabled={!selectedSiteId || isAssociating}
          onClick={handleNext}
        >
          {isAssociating ? t`Connecting device...` : t`Next`}
        </Button>
      </div>
    </div>
  );
};

export default SelectSiteStep;
